import { useState, useEffect, useCallback, useMemo } from 'react';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';
import { useSharedReportDateFilter } from '../redux/reportFilters/reportFiltersHooks';
import {
    todayStr,
    getLiveCheckInOverview,
    getCheckInOverviewTotals,
    getCheckInOverviewHourly,
    getCheckInOverviewGuestsHourly,
    getCheckInOverviewDailyAndGuest,
    getCheckInOverviewDineInGroup,
    getCheckInOverviewGuestSize,
    getCheckInOverviewAvgWaitTimeGroup,
    getCheckInOverviewTableDetails,
    getCheckInOverviewTopCustomer,
} from '../services/reportsApi';
import {
    useStoreTimeZone,
    getStoreTimeZone,
    getAnchorDate,
    getThisMonthRange,
} from '../utils/dateTimeUtils';
import { resolveTokenRange, normalizeToken } from '../utils/reportPeriod';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const CHANNEL_COLORS = ['#D23A3B', '#16A34A', '#D97706', '#6366F1'];

function formatHourLabel(hourStr: string): string {
    const h = parseInt(hourStr, 10);
    if (Number.isNaN(h)) return hourStr;
    const next = h + 1;
    const startNum = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const endNum = next === 24 ? 12 : next > 12 ? next - 12 : next;
    const period = next === 24 || next < 12 ? 'AM' : 'PM';
    return `${startNum}-${endNum} ${period}`;
}

/** Single-hour label: 12 AM, 1 AM, 2 AM, ... 11 PM (for chart axes) */
function formatHourLabelShort(hourStr: string): string {
    const h = parseInt(hourStr, 10);
    if (Number.isNaN(h)) return hourStr;
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function normalizeGroupSizeLabel(groupSize: any): string {
    if (groupSize == null) return '';
    const raw = String(groupSize).trim();
    if (!raw) return '';
    const hasPlus = raw.includes('+');
    const numeric = Number(raw.replace(/[^\d]/g, ''));
    if (Number.isFinite(numeric) && numeric > 0) {
        if (hasPlus || numeric > 8) return '8+';
        return String(numeric);
    }
    const cleaned = raw.replace(/^group\s*of\s*/i, '').replace(/\s+/g, '');
    return cleaned || raw;
}

function parseGroupSortValue(sizeLabel: any): number {
    if (!sizeLabel) return Number.POSITIVE_INFINITY;
    if (String(sizeLabel).includes('+')) return 999;
    const num = Number(String(sizeLabel).replace(/[^\d]/g, ''));
    return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
}

/** Derive chart/KPI data from overview API responses (shared by CheckinReport, Desktop, Mobile) */
export function deriveCheckInData(overview: any, options: { isToday?: boolean } = {}) {
    const totals = overview?.totals || {};
    const today = todayStr();
    const isToday = options?.isToday === true;
    const liveOverview = (isToday && overview?.liveOverview && typeof overview.liveOverview === 'object') ? overview.liveOverview : {};

    const liveTotalActive = Number(liveOverview.totalActive) || 0;
    const liveBooked = Number(liveOverview.booked) || 0;
    const liveConfirmed = Number(liveOverview.confirmed) || 0;
    const liveArrived = Number(liveOverview.arrived) || 0;
    const liveLateArrival = Number(liveOverview.lateArrival ?? liveOverview.lateShow) || 0;
    const liveTodayCount = Number(overview?.liveToday?.totalElements) || 0;
    const liveCurrentCount = Number(overview?.liveTable?.totalElements) || 0;

    const totalCheckin = isToday ? liveTotalActive : (Number(totals.totalCheckin) || 0);
    const totalGuests = isToday
        ? ((overview?.liveGuestCount || []).reduce((sum, row) => sum + (Number(row?.guestCount) || 0), 0))
        : (Number(totals.totalGuests) || 0);
    const totalCencellation = isToday ? liveLateArrival : (Number(totals.totalCencellation) || 0);
    const avgWait = isToday
        ? (overview?.liveAvgWaitTime != null ? Number(overview.liveAvgWaitTime) : null)
        : (totals.avgWaitTime != null ? Number(totals.avgWaitTime) : null);
    const cancelPct = isToday
        ? String(totalCencellation)
        : (totalCheckin ? ((totalCencellation / totalCheckin) * 100).toFixed(2) : '0');
    const seatedPct = totalCheckin ? (((totalCheckin - totalCencellation) / totalCheckin) * 100).toFixed(2) : '0';
    const avgParty = totalCheckin ? (totalGuests / totalCheckin).toFixed(2) : '0';

    const kpis = [
        {
            key: 'checkins',
            label: isToday ? 'TOTAL ACTIVE' : 'TOTAL CHECK-INS',
            value: String(totalCheckin),
            delta: isToday ? null : (totals.totalCheckinPercentage != null ? Number(totals.totalCheckinPercentage) : null),
            icon: 'how_to_reg',
        },
        {
            key: 'guests',
            label: isToday ? 'BOOKED' : 'TOTAL GUESTS',
            value: isToday ? String(liveBooked) : String(totalGuests),
            delta: isToday ? null : (totals.totalGuestsPercentage != null ? Number(totals.totalGuestsPercentage) : null),
            icon: 'groups',
        },
        {
            key: 'avgParty',
            label: isToday ? 'CONFIRMED' : 'AVG PARTY SIZE',
            value: isToday ? String(liveConfirmed) : avgParty,
            delta: null,
            icon: 'person',
        },
        {
            key: 'cancels',
            label: isToday ? 'LATE ARRIVAL' : 'CANCELLATION RATE',
            value: isToday ? cancelPct : `${cancelPct}%`,
            delta: null,
            icon: 'person_cancel',
            invertDelta: true,
        },
        ...(isToday ? [{
            key: 'arrived',
            label: 'ARRIVED',
            value: String(liveArrived),
            delta: null,
            icon: 'event_available',
        }] : []),
        /* hidden in today/live view */
        ...(!isToday ? [{
            key: 'totalCancel',
            label: 'TOTAL CANCELLATIONS',
            value: String(totalCencellation),
            delta: null,
            icon: 'cancel',
            invertDelta: true,
        }] : []),
        /* hidden in today/live view */
        ...(!isToday ? [{
            key: 'waitTime',
            label: 'AVG WAIT TIME',
            value: `${avgWait ?? 0} min`,
            delta: null,
            icon: 'schedule',
            invertDelta: true,
        }] : []),
        /* today check-ins and live check-ins hidden in today/live view */
        ...(!isToday ? [
            { key: 'dinein', label: 'AVG DINE-IN', value: '—', delta: null, icon: 'restaurant' },
            { key: 'peakHour', label: 'PEAK HOUR', value: '—', delta: null, icon: 'trending_up' },
        ] : []),
        /* hidden in today/live view */
        ...(!isToday ? [{ key: 'seatedEst', label: 'SEATED RATE*', value: `${seatedPct}%`, delta: null, icon: 'event_seat' }] : []),
    ];

    const normalizeHour = (v) => {
        const raw = v != null ? Number(v) : NaN;
        return Number.isFinite(raw) ? String(Math.min(23, Math.max(0, Math.floor(raw)))) : '';
    };
    const hourlyRaw = overview?.hourly || [];
    const byHour = {};
    hourlyRaw.forEach((r) => {
        const h = normalizeHour(r.checkinHour);
        if (h === '') return;
        if (!byHour[h]) byHour[h] = { checkins: 0, guests: 0 };
        byHour[h].checkins += Number(r.totalCheckins) || 0;
    });
    const hourlyGuestsRaw = overview?.hourlyGuests || [];
    console.log('[deriveCheckInData] hourlyGuestsRaw length:', hourlyGuestsRaw.length, 'sample:', hourlyGuestsRaw.slice(0, 5));
    hourlyGuestsRaw.forEach((r) => {
        const h = normalizeHour(r.checkinHour);
        if (h === '') return;
        if (!byHour[h]) byHour[h] = { checkins: 0, guests: 0 };
        byHour[h].guests += Number(r.totalGuests) || 0;
    });
    console.log('[deriveCheckInData] byHour (after merge):', JSON.parse(JSON.stringify(byHour)));
    const hourKeys = Object.keys(byHour).sort((a, b) => Number(a) - Number(b));
    const allHours = Array.from({ length: 24 }, (_, i) => String(i));
    const hourly = allHours.map((h) => {
        const bucket = byHour[h] || { checkins: 0, guests: 0 };
        return {
            h: formatHourLabelShort(h),
            v: bucket.checkins,
            checkins: bucket.checkins,
            guests: bucket.guests,
        };
    });

    const checkinDetailsRaw = overview?.checkinDetails || [];
    const channelSums: Record<string, number> = {};
    checkinDetailsRaw.forEach((r) => {
        const name = r.channelName || r.channel || r.checkinChannel || r.source || 'Other';
        channelSums[name] = (channelSums[name] || 0) + 1;
    });
    const channelTotal = Object.values(channelSums).reduce((s, n) => s + n, 0);
    const channels = Object.entries(channelSums).map(([name], i) => ({
        name,
        value: channelSums[name],
        pct: channelTotal ? Math.round((channelSums[name] / channelTotal) * 100) : 0,
        count: channelSums[name],
        color: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
    }));

    const parseWaitRangeMid = (waitTime) => {
        if (waitTime == null) return null;
        const raw = String(waitTime).trim();
        if (!raw) return null;
        if (raw.includes('-')) {
            const parts = raw.split('-').map((x) => Number(String(x).replace(/[^\d.]/g, '')));
            const from = Number.isFinite(parts[0]) ? parts[0] : null;
            const to = Number.isFinite(parts[1]) ? parts[1] : null;
            if (from != null && to != null) return (from + to) / 2;
        }
        const val = Number(raw.replace(/[^\d.]/g, ''));
        return Number.isFinite(val) ? val : null;
    };

    const avgWaitTimeByGroup = (overview?.avgWaitTimeGroup || [])
        .map((row) => {
            const normalizedSize = normalizeGroupSizeLabel(row?.groupSize);
            const waitTime = row?.waitTime != null ? String(row.waitTime).trim() : '';
            const checkInCount = Number(row?.checkInCount) || 0;
            return {
                size: normalizedSize,
                label: normalizedSize ? `Group of ${normalizedSize}` : 'Group',
                checkInCount,
                waitTime,
                sortValue: parseGroupSortValue(normalizedSize),
            };
        })
        .filter((row) => row.size && row.waitTime)
        .sort((a, b) => a.sortValue - b.sortValue)
        .map(({ sortValue, ...row }) => row);

    const waitByGroup = {};
    avgWaitTimeByGroup.forEach((row) => {
        const group = row?.size;
        if (!group) return;
        const wait = parseWaitRangeMid(row?.waitTime);
        if (wait == null) return;
        // Keep group wait-time directly from API bucket (no cross-row averaging).
        waitByGroup[group] = Math.round(wait);
    });

    const guestSizeRaw = (overview?.guestSize || []).filter((r) => Number(r.groupSize) !== 0 && Number(r.guestSize) !== 0);
    const partySize = guestSizeRaw.map((r) => {
        const sizeNum = Number(r.groupSize);
        const guestNum = Number(r.guestSize) || 0;
        const normalizedGroupKey = sizeNum >= 8 ? '8+' : String(Number(r.groupSize));
        const avgWaitForGroup = waitByGroup[normalizedGroupKey] ?? 0;
        return {
            size: sizeNum >= 10 ? '8+' : String(r.groupSize),
            count: guestNum,
            wait: avgWaitForGroup,
            dinein: 0,
            guests: (sizeNum || 0) * guestNum,
        };
    });

    const dailyRaw = overview?.daily || [];
    const dailyCheckins = dailyRaw.map((r) => {
        const date = r.day ? new Date(r.day) : null;
        const valid = date && Number.isFinite(date.getTime());
        const d = valid ? date.toLocaleDateString('en-US', { weekday: 'short' }) : (r.day ? String(r.day).slice(0, 10) : '—');
        return { d, checkins: Number(r.totalCheckins) || 0, guests: Number(r.totalGuests) || 0 };
    });
    const guestVolumeTrend = dailyRaw.map((r) => {
        const date = r.day ? new Date(r.day) : null;
        const valid = date && Number.isFinite(date.getTime());
        const d = valid ? date.toLocaleDateString('en-US', { weekday: 'short' }) : (r.day ? String(r.day).slice(0, 10) : '—');
        return { d, guests: Number(r.totalGuests) || 0, checkins: Number(r.totalCheckins) || 0 };
    });

    const hourlySummary = allHours.map((h) => {
        const bucket = byHour[h] || { checkins: 0, guests: 0 };
        return {
            hour: formatHourLabelShort(h),
            checkins: bucket.checkins,
            guests: bucket.guests,
            cancels: 0,
            dinein: 0,
        };
    }).reverse();

    const dineInGroupRaw = (overview?.dineInGroup || []).filter((r) => Number(r.groupSize) !== 0 && Number(r.avgDineInDuration) !== 0);
    const byGroupDineIn: Record<string, number> = {};
    dineInGroupRaw.forEach((r: any) => {
        const g = r.groupSize;
        byGroupDineIn[g] = (byGroupDineIn[g] || 0) + (Number(r.avgDineInDuration) || 0);
    });
    const dineinBySize = Object.entries(byGroupDineIn).map(([size, total]) => ({
        size: Number(size) >= 10 ? '8+' : size,
        duration: Math.round(total),
    })).sort((a, b) => Number(a.size) - Number(b.size));

    const peakHourBucket = hourly.reduce((best, row) => {
        if (!best || (row.checkins || 0) > (best.checkins || 0)) return row;
        return best;
    }, null);
    if (!isToday) {
        const avgDineIn = dineinBySize.length
            ? Math.round(dineinBySize.reduce((sum, item) => sum + (Number(item.duration) || 0), 0) / dineinBySize.length)
            : null;
        const dineInKpi = kpis.find((k) => k.key === 'dinein');
        if (dineInKpi) dineInKpi.value = avgDineIn != null ? `${avgDineIn} min` : '—';
        const peakHourKpi = kpis.find((k) => k.key === 'peakHour');
        if (peakHourKpi) peakHourKpi.value = (peakHourBucket?.checkins > 0 ? peakHourBucket.h : null) ?? '—';
    }

    return {
        kpis,
        hourly,
        partySize,
        avgWaitTimeByGroup,
        channels,
        cancelTrend: [],
        cancelTrendFull: [],
        heatmap: { days: ['M', 'T', 'W', 'T', 'F', 'S', 'S'], dFull: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], hours: [], hFull: [], data: [], dataFull: [] },
        dailyCheckins,
        waitByDay: [],
        dineinByDay: [],
        dineinBySize,
        cancelByChannel: [],
        cancelByHour: [],
        hourlySummary,
        guestVolumeTrend,
    };
}

// Delegates to the single GLOBAL-DP resolver (utils/reportPeriod) for every token in the
// global vocabulary, so a range carried in from another report resolves the same way here
// as it does there. Historical presets stay anchored on the store-timezone anchor date
// (5 AM cutoff) — see getAnchorDate / DateProdIssue.md.
//
// 'month' and 'quarter' are Check-in-only aliases with no equivalent in the global
// quick-select vocabulary, so they are still handled locally rather than being pushed into
// the shared shape registry.
function getDateRangeForPeriod(period: string, timeZoneCd: string = getStoreTimeZone()): { startDate: string; endDate: string } {
    const normalized = normalizeToken(period);
    const tStr = todayStr(timeZoneCd);
    if (normalized === 'today') return { startDate: tStr, endDate: tStr };
    if (normalized === 'month') return getThisMonthRange(timeZoneCd);
    if (normalized === 'quarter') {
        // Quarter containing the anchor date, up to the anchor.
        const anchor = getAnchorDate(timeZoneCd);
        const [year, month] = anchor.split('-');
        const qStartMonth = Math.floor((Number(month) - 1) / 3) * 3 + 1;
        return { startDate: `${year}-${String(qStartMonth).padStart(2, '0')}-01`, endDate: anchor };
    }
    return resolveTokenRange(period, timeZoneCd) ?? { startDate: tStr, endDate: tStr };
}

function ensureArray(val: any): any[] {
    if (Array.isArray(val)) return val;
    if (val && typeof val === 'object' && val.content) return val.content;
    if (val && typeof val === 'object' && val.data) return Array.isArray(val.data) ? val.data : [val.data];
    return [];
}

function ensureObject(val: any): Record<string, any> {
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    return {};
}

/**
 * Hook for Check-In Overview (historical) report.
 * Requires locationId (from branch) and date range; fetches totals, hourly, daily, group size, etc.
 */
export function useCheckInOverview(initialPeriod: string = '7D') {
    const { selectedBranch } = useBranch();
    const { auth } = useAuth();
    const locationId = useMemo(
        () => selectedBranch?.id ?? selectedBranch?.locationId ?? '',
        [selectedBranch]
    );
    const token = useMemo(() => findToken(auth), [auth]);

    const [datePeriod, setDatePeriod] = useSharedReportDateFilter(initialPeriod);
    const storeTz = useStoreTimeZone();
    const { startDate, endDate } = useMemo(() => getDateRangeForPeriod(datePeriod, storeTz), [datePeriod, storeTz]);
    const isToday = useMemo(() => {
        const normalized = String(datePeriod || '').trim().toLowerCase().replace(/\s+/g, '');
        return normalized === 'today';
    }, [datePeriod]);

    const [overview, setOverview] = useState<any>({
        totals: null,
        hourly: [],
        hourlyGuests: [],
        daily: [],
        dineInGroup: [],
        guestSize: [],
        avgWaitTimeGroup: [],
        checkinDetails: [],
        liveOverview: null,
        liveGuestCount: [],
        liveStatus: [],
        liveGroupAvgWaitTime: [],
        liveTable: null,
        liveToday: null,
        liveAvgWaitTime: null,
    });

    console.log('[useCheckInOverview] overview', overview);

    // Start in loading state so the shimmer renders on first paint.
    // Without this, the content layer mounts briefly before fetchOverview sets
    // loading=true, causing child components (e.g. LiveTodayCheckinTables) to
    // mount → unmount → remount, firing their effects twice.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchOverview = useCallback(async () => {
        if (!locationId) {
            setOverview({
                totals: null, hourly: [], hourlyGuests: [], daily: [], dineInGroup: [], guestSize: [], avgWaitTimeGroup: [], checkinDetails: [],
                liveOverview: null, liveGuestCount: [], liveStatus: [], liveGroupAvgWaitTime: [], liveTable: null, liveToday: null, liveAvgWaitTime: null,
            });
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            if (isToday) {
                // Today mode: the only data rendered from this hook is the KPI cards
                // (TOTAL ACTIVE, BOOKED, CONFIRMED, ARRIVED, LATE ARRIVAL) — all sourced from
                // liveOverview. The Live/Today check-in tables fetch their own data
                // from LiveTodayCheckinTables, and all other charts are hidden in
                // Today mode, so we skip every other API call here.
                const liveOverview = await getLiveCheckInOverview({ locationId, token }).then(ensureObject).catch(() => ({}));
                setOverview({
                    totals: null, hourly: [], hourlyGuests: [], daily: [], dineInGroup: [], guestSize: [], avgWaitTimeGroup: [], checkinDetails: [],
                    liveOverview: Object.keys(liveOverview || {}).length ? liveOverview : null,
                    liveGuestCount: [], liveStatus: [], liveGroupAvgWaitTime: [],
                    liveTable: null, liveToday: null,
                    liveAvgWaitTime: null,
                });
                return;
            }

            // Historical mode (Yesterday / 7D / 30D / This Month / Custom)
            const opts = { locationId, startDate, endDate, token };
            const [
                totals,
                hourly,
                hourlyGuests,
                daily,
                dineInGroup,
                guestSize,
                avgWaitTimeGroup,
                checkinDetails,
            ] = await Promise.all([
                getCheckInOverviewTotals(opts).then(ensureObject).catch(() => ({})),
                getCheckInOverviewHourly(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewGuestsHourly(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewDailyAndGuest(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewDineInGroup(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewGuestSize(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewAvgWaitTimeGroup(opts).then(ensureArray).catch(() => []),
                getCheckInOverviewTableDetails({ ...opts, page: 1, size: 1000 }).then((r: any) => Array.isArray(r?.content) ? r.content : []).catch(() => []),
            ]);

            console.log('/checkin/overview/hourlyCheckin response:', hourly);
            console.log('/checkin/overview/hourlyGuests response:', hourlyGuests);

            setOverview({
                totals: Object.keys(totals).length ? totals : null,
                hourly,
                hourlyGuests,
                daily,
                dineInGroup,
                guestSize,
                avgWaitTimeGroup,
                checkinDetails: Array.isArray(checkinDetails) ? checkinDetails : [],
                liveOverview: null,
                liveGuestCount: [],
                liveStatus: [],
                liveGroupAvgWaitTime: [],
                liveTable: null,
                liveToday: null,
                liveAvgWaitTime: null,
            });
        } catch (err) {
            setError(err?.message || 'Failed to load check-in overview');
            setOverview({
                totals: null, hourly: [], hourlyGuests: [], daily: [], dineInGroup: [], guestSize: [], avgWaitTimeGroup: [], checkinDetails: [],
                liveOverview: null, liveGuestCount: [], liveStatus: [], liveGroupAvgWaitTime: [], liveTable: null, liveToday: null, liveAvgWaitTime: null,
            });
        } finally {
            setLoading(false);
        }
    }, [locationId, startDate, endDate, token, isToday]);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    return {
        overview,
        loading,
        error,
        refetch: fetchOverview,
        datePeriod,
        setDatePeriod,
        startDate,
        endDate,
        isToday,
        locationId,
        token,
    };
}

/**
 * Fetch check-in details table (paginated). Debounce search on your side if needed.
 */
export async function fetchCheckInDetails({ locationId, startDate, endDate, search, page = 1, size = 10, token }: any) {
    if (!locationId || !startDate || !endDate) return { content: [], totalPages: 0, totalElements: 0 };
    const opts = { locationId, startDate, endDate, search: search || undefined, page, size, token };
    const res = await getCheckInOverviewTableDetails(opts);
    const content = Array.isArray(res?.content) ? res.content : [];
    return {
        content,
        totalPages: res?.totalPages ?? 0,
        totalElements: res?.totalElements ?? 0,
    };
}

/**
 * Fetch top repeat customers (paginated).
 */
export async function fetchTopCustomers({ locationId, startDate, endDate, search, page = 1, size = 10, token }: any) {
    if (!locationId || !startDate || !endDate) return { content: [], totalPages: 0, totalElements: 0 };
    const opts = { locationId, startDate, endDate, search: search || undefined, page, size, token };
    const res = await getCheckInOverviewTopCustomer(opts);
    const content = Array.isArray(res?.content) ? res.content : [];
    return {
        content,
        totalPages: res?.totalPages ?? 0,
        totalElements: Math.min(res?.totalElements ?? 0, 500),
    };
}
